import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-5aa0daf8"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "experience-availability" }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("hr", { class: "experience-availability__section-divider" }, null, -1))
const _hoisted_3 = { class: "experience-availability__section-wrapper" }
const _hoisted_4 = {
  key: 0,
  class: "experience-availability__section-loading"
}
const _hoisted_5 = {
  key: 1,
  class: "experience-availability__section"
}
const _hoisted_6 = { class: "experience-availability__sub-section experience-availability__sub-section-start" }
const _hoisted_7 = { class: "experience-availability__choice-section" }
const _hoisted_8 = { class: "experience-availability__choice-wrapper" }
const _hoisted_9 = { class: "experience-availability__sub-section experience-availability__sub-section-duration" }
const _hoisted_10 = { class: "experience-availability__choice-section" }
const _hoisted_11 = { class: "experience-availability__choice-wrapper" }

import BaseTag from '@/components/BaseTag.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { formatMinutesToDuration } from '@/utils/formatters'
import { PropType, ref, watch } from 'vue'
import {
  ExperiencePeriodAvailabilityPayload,
  ExperienceTimeslot,
  PublicServiceDetails,
} from '@/models/public/service.model'
import { useFetchExperienceDetailedAvailability } from '@/queries/public/services'
import { DurationMinutes, StringDateTime } from '@/models/shared.model'
import { format, parseISO } from 'date-fns'


export default /*#__PURE__*/_defineComponent({
  __name: 'ExperienceAvailabilityPicker',
  props: {
  service: {
    type: Object as PropType<PublicServiceDetails>,
    required: true,
  },
  date: {
    type: Object as PropType<Date>,
    required: true,
  },
  groupSize: {
    type: Number,
    required: true,
  },
  time: {
    type: String,
    default: null,
  },
  duration: {
    type: Number,
    default: null,
  },
  excludedReservations: {
    type: Array as PropType<number[]>,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: null,
  },
},
  emits: ['fetchInfo'],
  setup(__props, { emit }) {

const props = __props



const experienceTimeSlots = ref<ExperienceTimeslot[] | null>(null)

const experienceTimeChips = ref<StringDateTime[]>([])
const selectedTime = ref(null)
const experienceDurationChips = ref<DurationMinutes[]>([])
const selectedDuration = ref(null)

const selectedTimeSlot = ref<ExperienceTimeslot | null>(null)

const {
  mutateAsync: fetchExperienceDetailedAvailability,
  isLoading: isLoadingTimeSlots,
} = useFetchExperienceDetailedAvailability(props.service.hash_id)

function fetchTimeSlots(date: Date) {
  fetchExperienceDetailedAvailability({
    date: date,
    group_size: props.groupSize,
    excluded_reservations: props.excludedReservations,
  })
    .then((data) => {
      experienceTimeSlots.value = data
      setExperienceTimeChips(data)
      setExperienceDurationChips(data)
    })
    .catch(() => {
      experienceTimeSlots.value = []
    })
}

function setExperienceTimeChips(timeSlotList: ExperienceTimeslot[]) {
  experienceTimeChips.value = []
  timeSlotList.forEach((timeSlot: ExperienceTimeslot) => {
    if (!experienceTimeChips.value.includes(timeSlot.start)) {
      experienceTimeChips.value.push(timeSlot.start)
    }
  })
}

function setExperienceDurationChips(timeSlotList: ExperienceTimeslot[]) {
  experienceDurationChips.value = []
  timeSlotList.forEach((timeSlot: ExperienceTimeslot) => {
    if (!experienceDurationChips.value.includes(timeSlot.duration)) {
      experienceDurationChips.value.push(timeSlot.duration)
    }
  })
}

function filterExperienceTimeSlotsByTime() {
  if (!experienceTimeSlots.value) return []
  return experienceTimeSlots.value.filter((timeSlot: ExperienceTimeslot) => {
    return timeSlot.start === selectedTime.value
  })
}

function filterExperienceTimeSlotsByDuration() {
  if (!experienceTimeSlots.value) return []
  return experienceTimeSlots.value.filter((timeSlot: ExperienceTimeslot) => {
    return timeSlot.duration === selectedDuration.value
  })
}

watch([() => selectedTime.value, () => selectedDuration.value], () => {
  if (!experienceTimeSlots.value) return null
  if (selectedTime.value && selectedDuration.value) {
    // TimeSlot is selected and experienceInfo can be fetched
    selectedTimeSlot.value =
      experienceTimeSlots.value.find(
        (timeSlot: ExperienceTimeslot) =>
          timeSlot.start === selectedTime.value &&
          timeSlot.duration === selectedDuration.value,
      ) ?? null
  } else if (selectedTime.value && !selectedDuration.value) {
    // Time hase been selected and available duration is refiltered
    setExperienceDurationChips(filterExperienceTimeSlotsByTime())
  } else if (selectedDuration.value && !selectedTime.value) {
    // Duration hase been selected and available time is refiltered
    setExperienceTimeChips(filterExperienceTimeSlotsByDuration())
  } else {
    // Set default chips
    emit('fetchInfo', null)
    setExperienceTimeChips(experienceTimeSlots.value)
    setExperienceDurationChips(experienceTimeSlots.value)
  }
})

watch([() => selectedTimeSlot.value, () => props.groupSize], () => {
  if (selectedTimeSlot.value) {
    const payload: ExperiencePeriodAvailabilityPayload = {
      start: selectedTimeSlot.value.start,
      end: selectedTimeSlot.value.end,
      group_size: props.groupSize,
    }

    emit('fetchInfo', payload)
  }
})

watch(
  () => props.date,
  () => {
    selectedTime.value = props.time || null
    selectedDuration.value = props.duration || null
    selectedTimeSlot.value = null
    emit('fetchInfo', null)
    fetchTimeSlots(props.date)
  },
  { immediate: true },
)

watch(
  () => props.time,
  () => {
    selectedTime.value = props.time
  },
  { immediate: true },
)

watch(
  () => props.duration,
  () => {
    selectedDuration.value = props.duration
  },
  { immediate: true },
)



return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _hoisted_2,
    _createElementVNode("div", _hoisted_3, [
      (_unref(isLoadingTimeSlots))
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createVNode(LoadingSpinner)
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("h6", null, _toDisplayString(_ctx.$t('start')), 1),
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("div", _hoisted_8, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(experienceTimeChips.value, (dateTime, timeIndex) => {
                    return (_openBlock(), _createBlock(BaseTag, {
                      key: timeIndex,
                      label: _unref(format)(_unref(parseISO)(dateTime), 'HH:mm'),
                      "hide-close": "",
                      disabled: __props.disabled,
                      class: _normalizeClass(["experience-availability__choice", [
                  selectedTime.value === dateTime &&
                    'experience-availability__choice-selected',
                ]]),
                      onClick: ($event: any) => (
                  selectedTime.value = selectedTime.value !== dateTime ? dateTime : null
                )
                    }, null, 8, ["label", "disabled", "class", "onClick"]))
                  }), 128))
                ])
              ])
            ]),
            _createElementVNode("div", _hoisted_9, [
              _createElementVNode("h6", null, _toDisplayString(_ctx.$t('duration')), 1),
              _createElementVNode("div", _hoisted_10, [
                _createElementVNode("div", _hoisted_11, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(experienceDurationChips.value, (duration, durationIndex) => {
                    return (_openBlock(), _createBlock(BaseTag, {
                      key: durationIndex,
                      label: _unref(formatMinutesToDuration)(duration),
                      "hide-close": "",
                      disabled: __props.disabled,
                      class: _normalizeClass(["experience-availability__choice", [
                  selectedDuration.value === duration &&
                    'experience-availability__choice-selected',
                ]]),
                      onClick: ($event: any) => (
                  selectedDuration.value =
                    selectedDuration.value !== duration ? duration : null
                )
                    }, null, 8, ["label", "disabled", "class", "onClick"]))
                  }), 128))
                ])
              ])
            ])
          ]))
    ])
  ]))
}
}

})